import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapPin, RefreshCw, Users } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/* ------------------------------------------------------------------ */
/* Leaflet loader — CDN-based to avoid adding a heavy npm dep.        */
/* ------------------------------------------------------------------ */
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";

async function loadLeaflet() {
  if (typeof window === "undefined") return null;
  if (window.L) return window.L;
  if (!document.head.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = LEAFLET_CSS;
    document.head.appendChild(css);
  }
  await new Promise((resolve, reject) => {
    let s = document.head.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (s) { s.addEventListener("load", resolve); s.addEventListener("error", reject); return; }
    s = document.createElement("script");
    s.src = LEAFLET_JS;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.L;
}

const ZONE_COLORS = {
  at_venue: "#22c55e",
  near_venue: "#c49a6c",
  in_cyprus: "#1f6feb",
  far: "#8b8b8b",
};

const ZONE_LABEL = {
  at_venue: "At venue",
  near_venue: "Near venue",
  in_cyprus: "In Cyprus",
  far: "Far away",
};

function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const AdminLocationMap = () => {
  const mapEl = useRef(null);
  const mapObj = useRef(null);
  const markerLayer = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: res } = await api.get("/admin/location/pings");
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load pings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Initialise the map exactly once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = await loadLeaflet();
      if (cancelled || !L || !mapEl.current || mapObj.current) return;
      const map = L.map(mapEl.current, { scrollWheelZoom: false }).setView([34.9853, 34.0102], 4);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);
      markerLayer.current = L.layerGroup().addTo(map);
      mapObj.current = map;
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-render markers whenever the data changes.
  useEffect(() => {
    (async () => {
      const L = window.L;
      if (!L || !mapObj.current || !markerLayer.current) return;
      markerLayer.current.clearLayers();

      // Venue pin — highlighted with an accent ring.
      if (data?.venue) {
        const venueIcon = L.divIcon({
          className: "",
          html: `<div style="transform:translate(-50%,-100%);position:relative">
                  <div style="width:22px;height:22px;border-radius:9999px;background:#181614;border:3px solid #c49a6c;box-shadow:0 4px 12px rgba(0,0,0,.35)"></div>
                  <div style="position:absolute;left:50%;top:100%;transform:translate(-50%,0);width:2px;height:14px;background:#c49a6c"></div>
                </div>`,
          iconSize: [22, 36],
          iconAnchor: [11, 36],
        });
        L.marker([data.venue.lat, data.venue.lon], { icon: venueIcon, zIndexOffset: 1000 })
          .addTo(markerLayer.current)
          .bindTooltip(`Venue · ${data.venue.name}`, { direction: "top", offset: [0, -30] });
      }

      const pings = data?.pings || [];
      const bounds = [];
      pings.forEach((p) => {
        if (typeof p.lat !== "number" || typeof p.lon !== "number") return;
        const color = ZONE_COLORS[p.zone] || ZONE_COLORS.far;
        const label = (p.guest_name || "?").slice(0, 1).toUpperCase();
        const icon = L.divIcon({
          className: "",
          html: `<div style="transform:translate(-50%,-100%);position:relative">
                  <div style="min-width:28px;height:28px;padding:0 6px;border-radius:14px;background:${color};color:#fff;font:600 11px/28px 'Inter',sans-serif;text-align:center;border:2px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,.3)">${label}</div>
                </div>`,
          iconSize: [28, 34],
          iconAnchor: [14, 34],
        });
        const m = L.marker([p.lat, p.lon], { icon }).addTo(markerLayer.current);
        m.on("click", () => setSelected(p));
        m.bindTooltip(
          `${p.guest_name || "Guest"} · ${ZONE_LABEL[p.zone] || p.zone} · ${relTime(p.last_ping_at)}`,
          { direction: "top", offset: [0, -28] }
        );
        bounds.push([p.lat, p.lon]);
      });

      if (bounds.length > 0) {
        try {
          mapObj.current.fitBounds(bounds.concat([[data.venue.lat, data.venue.lon]]), {
            padding: [40, 40], maxZoom: 12,
          });
        } catch { /* ignore */ }
      } else if (data?.venue) {
        mapObj.current.setView([data.venue.lat, data.venue.lon], 10);
      }
    })();
  }, [data]);

  const summary = useMemo(() => {
    const pings = data?.pings || [];
    const counts = { at_venue: 0, near_venue: 0, in_cyprus: 0, far: 0 };
    pings.forEach((p) => { counts[p.zone] = (counts[p.zone] || 0) + 1; });
    return { total: pings.length, ...counts };
  }, [data]);

  return (
    <div className="space-y-5" data-testid="admin-location-map">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="border border-border rounded-lg p-3">
          <p className="editorial-kicker text-foreground/55">Guests tracked</p>
          <p className="font-serif-display text-2xl mt-1 flex items-center gap-2">
            <Users className="w-4 h-4 text-accent" /> {summary.total}
          </p>
        </div>
        {[
          { k: "at_venue", label: "At venue" },
          { k: "near_venue", label: "Near venue" },
          { k: "in_cyprus", label: "In Cyprus" },
          { k: "far", label: "Far away" },
        ].map((z) => (
          <div key={z.k} className="border border-border rounded-lg p-3">
            <p className="editorial-kicker text-foreground/55 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: ZONE_COLORS[z.k] }} />
              {z.label}
            </p>
            <p className="font-serif-display text-2xl mt-1">{summary[z.k] || 0}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="editorial-kicker text-foreground/55">Live positions</p>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="admin-location-refresh">
          <RefreshCw className={"w-3.5 h-3.5 mr-2 " + (loading ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>

      {err && <p className="text-sm text-destructive">{err}</p>}

      <div className="relative rounded-lg overflow-hidden border border-border">
        <div ref={mapEl} className="w-full h-[520px] bg-foreground/[0.04]" data-testid="admin-location-leaflet" />
        {selected && (
          <div
            className="absolute top-3 right-3 max-w-xs w-[280px] bg-background/95 backdrop-blur border border-border rounded-lg shadow-xl p-4"
            data-testid="admin-location-selected"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-serif-display text-lg leading-tight truncate">
                  {selected.guest_name || "Anonymous guest"}
                </p>
                <p className="editorial-kicker text-foreground/55 mt-0.5">
                  {selected.client_id?.slice(0, 10)}…
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-foreground/60 hover:text-foreground text-lg leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="mt-3 space-y-1.5 text-sm">
              <p className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: ZONE_COLORS[selected.zone] }} />
                <Badge variant="secondary">{ZONE_LABEL[selected.zone] || selected.zone}</Badge>
              </p>
              <p className="text-foreground/70">
                {selected.distance_venue_km?.toFixed(2)} km from venue
              </p>
              <p className="text-foreground/55 text-xs">
                Language <span className="uppercase">{selected.lang}</span> · Last seen {relTime(selected.last_ping_at)}
              </p>
              {selected.accuracy_m && (
                <p className="text-foreground/50 text-[11px]">±{Math.round(selected.accuracy_m)} m accuracy</p>
              )}
              <p className="text-foreground/50 text-[11px] truncate" title={selected.user_agent}>
                {selected.user_agent}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Table view underneath, for a keyboard-friendly overview */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-location-table">
          <thead className="bg-foreground/[0.03]">
            <tr>
              <th className="text-left px-3 py-2 editorial-kicker text-foreground/55">Guest</th>
              <th className="text-left px-3 py-2 editorial-kicker text-foreground/55">Zone</th>
              <th className="text-left px-3 py-2 editorial-kicker text-foreground/55">Distance</th>
              <th className="text-left px-3 py-2 editorial-kicker text-foreground/55">Lang</th>
              <th className="text-left px-3 py-2 editorial-kicker text-foreground/55">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {(data?.pings || []).slice(0, 100).map((p) => (
              <tr key={p.client_id} className="border-t border-border/50 hover:bg-foreground/[0.02] cursor-pointer" onClick={() => setSelected(p)}>
                <td className="px-3 py-2 flex items-center gap-2">
                  <MapPin className="w-3 h-3" style={{ color: ZONE_COLORS[p.zone] }} />
                  {p.guest_name || <span className="text-foreground/40">Anonymous</span>}
                </td>
                <td className="px-3 py-2"><Badge variant="secondary">{ZONE_LABEL[p.zone] || p.zone}</Badge></td>
                <td className="px-3 py-2 text-foreground/70">{p.distance_venue_km?.toFixed(1)} km</td>
                <td className="px-3 py-2 uppercase text-foreground/60">{p.lang}</td>
                <td className="px-3 py-2 text-foreground/60">{relTime(p.last_ping_at)}</td>
              </tr>
            ))}
            {(!data?.pings || data.pings.length === 0) && !loading && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-foreground/50">No guests have shared their location yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminLocationMap;
